import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  Box, Typography, Table, TableHead, TableRow, TableCell, Button, Stack, Chip, IconButton, Tooltip, Alert, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, FormControlLabel, Switch, Grid, Card, CardContent,
} from '@mui/material';
import TableBody from '../components/PagedTableBody'; // pages rows beyond 10
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import TimelineIcon from '@mui/icons-material/Timeline';
import {
  ADMIN_ALL_TIMELINE_EVENTS_QUERY,
  CREATE_TIMELINE_EVENT_MUTATION,
  UPDATE_TIMELINE_EVENT_MUTATION,
  DELETE_TIMELINE_EVENT_MUTATION,
} from '../graphql/queries';

const STATUS_COLOR = { scheduled: 'info', in_progress: 'warning', completed: 'success', cancelled: 'error' };
const EVENT_TYPES  = ['ceremony','reception','ritual','meal','entertainment','transport','other'];

const EMPTY = { title: '', description: '', eventType: 'ceremony', startTime: '', endTime: '', allDay: false, status: 'scheduled', assignedTo: '', color: '#1976d2', sortOrder: 0 };

export default function AdminTimelinePage() {
  const [dialog, setDialog]   = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm]       = useState(EMPTY);
  const [snack, setSnack]     = useState({ open: false, msg: '', ok: true });

  const { data, loading, refetch } = useQuery(ADMIN_ALL_TIMELINE_EVENTS_QUERY, { fetchPolicy: 'cache-and-network' });

  const toast = (msg, ok = true) => setSnack({ open: true, msg, ok });

  const [create, { loading: creating }] = useMutation(CREATE_TIMELINE_EVENT_MUTATION, {
    onCompleted: () => { closeDialog(); refetch(); toast('Event created.'); },
    onError: e => toast(e.message, false),
  });
  const [update, { loading: updating }] = useMutation(UPDATE_TIMELINE_EVENT_MUTATION, {
    onCompleted: () => { closeDialog(); refetch(); toast('Event updated.'); },
    onError: e => toast(e.message, false),
  });
  const [remove] = useMutation(DELETE_TIMELINE_EVENT_MUTATION, {
    onCompleted: () => { refetch(); toast('Event deleted.'); },
    onError: e => toast(e.message, false),
  });

  const events = data?.allTimelineEvents || [];

  const openEdit = (ev) => {
    setEditing(ev);
    setForm({
      title: ev.title, description: ev.description || '', eventType: ev.eventType || 'ceremony',
      startTime: ev.startTime || '', endTime: ev.endTime || '',
      allDay: ev.allDay || false, status: ev.status || 'scheduled',
      assignedTo: ev.assignedTo || '', color: ev.color || '#1976d2', sortOrder: ev.sortOrder || 0,
    });
    setDialog(true);
  };
  const closeDialog = () => { setDialog(false); setEditing(null); setForm(EMPTY); };

  const handleSave = () => {
    if (!form.title.trim()) return;
    const vars = { ...form, sortOrder: parseInt(form.sortOrder) || 0 };
    if (editing) update({ variables: { id: editing.id, ...vars } });
    else create({ variables: vars });
  };

  const stats = {
    total: events.length,
    scheduled: events.filter(e => e.status === 'scheduled').length,
    completed: events.filter(e => e.status === 'completed').length,
    inProgress: events.filter(e => e.status === 'in_progress').length,
  };

  return (
    <Box p={3}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h4" fontWeight={700}>Timeline Events</Typography>
          <Typography variant="body2" color="text.secondary">All timeline events across all ceremonies</Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setEditing(null); setForm(EMPTY); setDialog(true); }}>
          New Event
        </Button>
      </Stack>

      {snack.open && (
        <Alert severity={snack.ok ? 'success' : 'error'} onClose={() => setSnack(s => ({ ...s, open: false }))} sx={{ mb: 2 }}>
          {snack.msg}
        </Alert>
      )}

      <Grid container spacing={2} mb={3}>
        {[
          { label: 'Total Events', value: stats.total, icon: <TimelineIcon color="primary" /> },
          { label: 'Scheduled', value: stats.scheduled, icon: <TimelineIcon color="info" /> },
          { label: 'In Progress', value: stats.inProgress, icon: <TimelineIcon color="warning" /> },
          { label: 'Completed', value: stats.completed, icon: <TimelineIcon color="success" /> },
        ].map(s => (
          <Grid item xs={6} sm={3} key={s.label}>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center', py: 1 }}>
                {s.icon}
                <Typography variant="h5" fontWeight={700}>{s.value}</Typography>
                <Typography variant="caption">{s.label}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {loading ? <CircularProgress /> : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Title</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Ceremony</TableCell>
              <TableCell>Start Time</TableCell>
              <TableCell>End Time</TableCell>
              <TableCell>Assigned To</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {events.map(ev => (
              <TableRow key={ev.id} hover>
                <TableCell><strong>{ev.title}</strong></TableCell>
                <TableCell>{ev.eventType || '—'}</TableCell>
                <TableCell>{ev.ceremony?.name || '—'}</TableCell>
                <TableCell>{ev.startTime ? new Date(ev.startTime).toLocaleString() : '—'}</TableCell>
                <TableCell>{ev.endTime ? new Date(ev.endTime).toLocaleString() : '—'}</TableCell>
                <TableCell>{ev.assignedTo || '—'}</TableCell>
                <TableCell><Chip label={ev.status || 'scheduled'} size="small" color={STATUS_COLOR[ev.status] || 'default'} /></TableCell>
                <TableCell align="right">
                  <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(ev)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                  <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => remove({ variables: { id: ev.id } })}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {events.length === 0 && (
              <TableRow><TableCell colSpan={8} align="center">No timeline events found.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      )}

      <Dialog open={dialog} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit Timeline Event' : 'New Timeline Event'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} mt={1}>
            <TextField label="Title" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} required fullWidth />
            <TextField label="Description" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} multiline rows={2} fullWidth />
            <TextField select label="Event Type" value={form.eventType} onChange={e => setForm(f => ({ ...f, eventType: e.target.value }))} fullWidth>
              {EVENT_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </TextField>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField label="Start Time" value={form.startTime} onChange={e => setForm(f => ({ ...f, startTime: e.target.value }))} type="datetime-local" fullWidth InputLabelProps={{ shrink: true }} />
              </Grid>
              <Grid item xs={6}>
                <TextField label="End Time" value={form.endTime} onChange={e => setForm(f => ({ ...f, endTime: e.target.value }))} type="datetime-local" fullWidth InputLabelProps={{ shrink: true }} />
              </Grid>
            </Grid>
            <TextField label="Assigned To" value={form.assignedTo} onChange={e => setForm(f => ({ ...f, assignedTo: e.target.value }))} fullWidth />
            <TextField select label="Status" value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))} fullWidth>
              {['scheduled','in_progress','completed','cancelled'].map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </TextField>
            <FormControlLabel
              control={<Switch checked={form.allDay} onChange={e => setForm(f => ({ ...f, allDay: e.target.checked }))} />}
              label="All Day Event"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={creating || updating} startIcon={<SaveIcon />}>
            {editing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
